<script setup lang="ts">
    import CanvasGuidettiView from '@/components/CanvasGuidettiView.vue';
    import BaseButton from '@/components/BaseButton.vue';
    import useConfigStore from '@/store/config.store';
    import { EAppStep } from '@/typescript/enums/EAppStep';
    import BaseIcon from '@/components/BaseIcon.vue';
    import { putProduct } from '@/api/product.api';
    import { t } from '@/translations/i18n';
    import Events from '@/services/Events';

    import useAnimationControl from '@/composables/useAnimationControl';
    import useStepper from '@/composables/useStepper';
    import useProductStore from '@/store/product.store';
    import { useZoomControl } from '@/composables/useZoomControl';
    import { generateThumbnail } from '@/utils/thumbnail';
    import BatDisplayContainer from '@/components/BatDisplayContainer.vue';
    import { computed, type PropType } from 'vue';
    import { storeToRefs } from 'pinia';
    import type { ITextField } from '@/typescript/interfaces/ITextField';
    import type { IImageField } from '@/typescript/interfaces/IImageField';
    import type { MeshStandardMaterial } from 'three';

    const { selectedProduct } = storeToRefs(useProductStore());

    const { isMobile, isAdmin, fonts, displayBatImage } = storeToRefs(useConfigStore());
    const { toggleAnimation, isAnimated, isPollOpen, openPoll, closePoll } = useAnimationControl();
    const { toggleZoom, isZoomed, zoom, unZoom } = useZoomControl();
    const { goToStep } = useStepper();

    const poleColor = computed(() => selectedProduct.value.configuration.poleColor);

    function goToEditStep() {
        goToStep(EAppStep.EDITION);
    }

    defineProps({
        updateCustomMaterial: {
            type: Function as PropType<(fields: ITextField[] | IImageField[], getImage: boolean) => Promise<void>>,
            required: true,
        },
        customMaterial: {
            type: Object as PropType<MeshStandardMaterial>,
            required: true,
        },
        showPreview: {
            type: Boolean,
            default: false,
        },
    });

    async function screenshot() {
        try {
            const screenShot = await generateThumbnail({ selectedProduct, fonts, poleColor });
            const namePrefix = 'guidetti_template_';
            const extension = `.jpg`;
            const now = new Date();
            const date = now.toISOString().split('T')[0];
            const time = `${now.getHours()}h-${now.getMinutes()}m-${now.getSeconds()}s`;
            const nameSuffix = `${date}_${time}`;

            const a = document.createElement('a');
            const url = URL.createObjectURL(screenShot);
            a.href = url;
            a.download = `${namePrefix}${nameSuffix}${extension}`;
            a.click();
        } catch (e) {
            console.error(e);
        }
    }

    async function save() {
        try {
            await putProduct(selectedProduct.value);
            Events.emit('call-toaster', { title: t('save_success'), type: 'success' });
        } catch (e) {
            Events.emit('call-toaster', { title: t('error_title'), body: `${e}`, type: 'error' });
        }
    }
</script>

<template>
    <div class="relative h-full w-full hover:cursor-pointer">
        <BatDisplayContainer
            v-if="displayBatImage"
            @hide-image="displayBatImage = false"
        />

        <div
            v-if="!isMobile"
            id="formContainer"
            class="pointer-events-none absolute left-0 top-0 z-20 h-full w-full"
        ></div>
        <div class="pointer-events-none absolute bottom-0 right-0 z-10 flex w-full flex-col items-end justify-end gap-2.5 p-4">
            <BaseButton
                class="pointer-events-auto h-10 w-10"
                @click="toggleAnimation"
            >
                <span class="sr-only">
                    {{ isAnimated ? $t('play') : $t('pause') }}
                </span>
                <BaseIcon
                    v-if="isAnimated"
                    size="lg"
                    name="PauseIcon"
                />
                <BaseIcon
                    v-else
                    size="lg"
                    name="PlayIcon"
                />
            </BaseButton>
            <BaseButton
                class="pointer-events-auto h-10 w-10"
                @click="isPollOpen ? closePoll() : openPoll()"
            >
                <span class="sr-only">
                    {{ isPollOpen ? $t('play') : $t('pause') }}
                </span>
                <BaseIcon
                    v-if="isPollOpen"
                    size="lg"
                    name="BoltSlashIcon"
                />
                <BaseIcon
                    v-else
                    size="lg"
                    name="BoltIcon"
                />
            </BaseButton>
            <BaseButton
                class="pointer-events-auto h-10 w-10"
                @click="toggleZoom"
            >
                <span class="sr-only">
                    {{ isZoomed ? $t('zoom') : $t('unzoom') }}
                </span>
                <img
                    v-if="isZoomed"
                    src="@/assets/images/picto/zoom-in.svg"
                    :alt="$t('unzoom_icon')"
                />
                <img
                    v-else
                    src="@/assets/images/picto/zoom-full.svg"
                    :alt="$t('unzoom_icon')"
                />
            </BaseButton>
            <BaseButton
                class="pointer-events-auto h-10 w-10"
                @click="zoom"
            >
                <span class="sr-only">
                    {{ $t('zoom') }}
                </span>
                <BaseIcon
                    size="lg"
                    name="MagnifyingGlassPlusIcon"
                />
            </BaseButton>
            <BaseButton
                class="pointer-events-auto h-10 w-10"
                @click="unZoom"
            >
                <span class="sr-only">
                    {{ $t('zoom') }}
                </span>
                <BaseIcon
                    size="lg"
                    name="MagnifyingGlassMinusIcon"
                />
            </BaseButton>
            <BaseButton
                v-if="isAdmin"
                class="pointer-events-auto h-10 w-10"
                @click="screenshot"
            >
                <span class="sr-only">
                    {{ $t('screenshot') }}
                </span>
                <BaseIcon
                    size="lg"
                    name="CameraIcon"
                />
            </BaseButton>
            <BaseButton
                v-if="isAdmin"
                class="pointer-events-auto px-2"
                :label="$t('save')"
                @click="save"
            >
                {{ $t('save') }}
            </BaseButton>
            <BaseButton
                class="pointer-events-auto h-10 w-10"
                @click="displayBatImage = true"
            >
                <strong>2D</strong>
            </BaseButton>
            <BaseButton
                v-if="isMobile"
                class="pointer-events-auto w-full"
                @click="goToEditStep"
            >
                {{ $t('edit_content_label') }}
            </BaseButton>
        </div>
        <CanvasGuidettiView
            :is-mobile
            :custom-material
            :update-custom-material
            :show-preview
        />
    </div>
</template>
